import type { StreamedTeam } from '../types';
import { PossessionArrow } from './PossessionArrow';
import { getTeamColorStyle, getTeamIndicatorStyle } from '../utils/colorUtils';

interface ScoreboardProps {
  homeTeam: StreamedTeam;
  awayTeam: StreamedTeam;
  period: number;
  clockTime: string | null;
  possessionArrow: 'home' | 'away' | null;
  isFinal?: boolean;
}

function formatClockTime(clockTime: string | number | null): string | null {
  if (clockTime === null || clockTime === undefined) return null;

  if (typeof clockTime === 'string') {
    if (/^\d{1,2}:\d{2}$/.test(clockTime)) {
      return clockTime;
    }
    const parsed = parseFloat(clockTime);
    if (!isNaN(parsed)) {
      clockTime = parsed;
    } else {
      return clockTime;
    }
  }

  if (typeof clockTime === 'number') {
    const totalSeconds = Math.floor(clockTime);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  return null;
}

export function Scoreboard({ homeTeam, awayTeam, period, clockTime, possessionArrow, isFinal }: ScoreboardProps) {
  const homeStyle = getTeamColorStyle(homeTeam.primaryColor);
  const awayStyle = getTeamColorStyle(awayTeam.primaryColor);
  const homeIndicator = getTeamIndicatorStyle(homeTeam.primaryColor);
  const awayIndicator = getTeamIndicatorStyle(awayTeam.primaryColor);
  const formattedClock = formatClockTime(clockTime);

  const getPeriodLabel = () => {
    if (isFinal) {
      if (period > 4) return `Final/OT${period - 4 > 1 ? period - 4 : ''}`;
      return 'Final';
    }
    return period > 4 ? `OT${period - 4}` : `P${period}`;
  };

  const getPeriodLabelFull = () => {
    if (isFinal) {
      if (period > 4) return `Final/OT${period - 4 > 1 ? period - 4 : ''}`;
      return 'Final';
    }
    return period > 4 ? `OT${period - 4}` : `Period ${period}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-2 sm:p-4 md:p-6">
      {/* Mobile: Compact header row with period, clock, possession */}
      <div className="flex items-center justify-center gap-2 mb-2 sm:hidden">
        <span className={`text-[10px] font-medium ${isFinal ? 'text-gray-700 font-bold' : 'text-gray-500'}`}>
          {getPeriodLabel()}
        </span>
        {!isFinal && formattedClock && (
          <span className="text-xs font-mono font-bold text-gray-800">
            {formattedClock}
          </span>
        )}
        {!isFinal && (
          <PossessionArrow
            possession={possessionArrow}
            homeColor={homeTeam.primaryColor}
            awayColor={awayTeam.primaryColor}
          />
        )}
      </div>

      {/* Scores Row */}
      <div className="flex items-center justify-center">
        {/* Home Team */}
        <div className="flex-1 text-center">
          {/* Score */}
          <div
            className="text-lg sm:text-4xl md:text-6xl font-bold rounded py-0.5 sm:py-2 mx-1"
            style={{
              backgroundColor: homeStyle.backgroundColor,
              color: homeStyle.color,
              border: homeStyle.border,
            }}
          >
            {homeTeam.score}
          </div>
          {/* Team abbreviation only on mobile */}
          <div className="mt-1 text-[10px] sm:text-sm font-bold text-gray-600 uppercase">
            <span className="sm:hidden">{homeTeam.abbreviation}</span>
            <span
              className="hidden sm:inline-block px-2 py-0.5 rounded"
              style={{
                backgroundColor: homeStyle.backgroundColor,
                color: homeStyle.color,
                border: homeStyle.border,
              }}
            >
              {homeTeam.name}
            </span>
          </div>
        </div>

        {/* Center divider - Desktop shows full info, mobile just VS */}
        <div className="px-1 sm:px-4">
          {/* Mobile: simple VS */}
          <div className="sm:hidden text-gray-300 text-[10px]">VS</div>

          {/* Desktop: Period/Clock/Possession */}
          <div className="hidden sm:flex flex-col items-center gap-1">
            {!isFinal && (
              <PossessionArrow
                possession={possessionArrow}
                homeColor={homeTeam.primaryColor}
                awayColor={awayTeam.primaryColor}
              />
            )}
            <div className={`text-xs sm:text-sm font-medium uppercase ${isFinal ? 'text-gray-700 font-bold text-base' : 'text-gray-500'}`}>
              {getPeriodLabelFull()}
            </div>
            {!isFinal && formattedClock && (
              <div className="text-xl sm:text-2xl md:text-3xl font-mono font-bold text-gray-800">
                {formattedClock}
              </div>
            )}
            <div className="text-gray-300 text-sm">VS</div>
          </div>
        </div>

        {/* Away Team */}
        <div className="flex-1 text-center">
          {/* Score */}
          <div
            className="text-lg sm:text-4xl md:text-6xl font-bold rounded py-0.5 sm:py-2 mx-1"
            style={{
              backgroundColor: awayStyle.backgroundColor,
              color: awayStyle.color,
              border: awayStyle.border,
            }}
          >
            {awayTeam.score}
          </div>
          {/* Team abbreviation only on mobile */}
          <div className="mt-1 text-[10px] sm:text-sm font-bold text-gray-600 uppercase">
            <span className="sm:hidden">{awayTeam.abbreviation}</span>
            <span
              className="hidden sm:inline-block px-2 py-0.5 rounded"
              style={{
                backgroundColor: awayStyle.backgroundColor,
                color: awayStyle.color,
                border: awayStyle.border,
              }}
            >
              {awayTeam.name}
            </span>
          </div>
        </div>
      </div>

      {/* Team Stats Row */}
      <div className="flex justify-between mt-2 sm:mt-6 pt-2 sm:pt-4 border-t border-gray-200">
        {/* Home Stats */}
        <div className="text-center">
          <div className="text-[9px] sm:text-xs text-gray-500">FOULS</div>
          <div className="text-xs sm:text-lg font-bold text-gray-700">{homeTeam.teamFouls}</div>
          <div className="flex gap-0.5 justify-center mt-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1 h-1 sm:w-2 sm:h-2 rounded-full"
                style={{
                  backgroundColor: i < homeTeam.timeoutsRemaining ? homeIndicator.backgroundColor : '#d1d5db',
                  border: i < homeTeam.timeoutsRemaining ? homeIndicator.border : 'none',
                  boxSizing: 'border-box',
                }}
              />
            ))}
          </div>
          <div className="text-[9px] sm:text-xs text-gray-400">TO</div>
        </div>

        {/* Away Stats */}
        <div className="text-center">
          <div className="text-[9px] sm:text-xs text-gray-500">FOULS</div>
          <div className="text-xs sm:text-lg font-bold text-gray-700">{awayTeam.teamFouls}</div>
          <div className="flex gap-0.5 justify-center mt-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1 h-1 sm:w-2 sm:h-2 rounded-full"
                style={{
                  backgroundColor: i < awayTeam.timeoutsRemaining ? awayIndicator.backgroundColor : '#d1d5db',
                  border: i < awayTeam.timeoutsRemaining ? awayIndicator.border : 'none',
                  boxSizing: 'border-box',
                }}
              />
            ))}
          </div>
          <div className="text-[9px] sm:text-xs text-gray-400">TO</div>
        </div>
      </div>
    </div>
  );
}
